import type { APIErrorContext } from "./universalAPIErrorHandler";
import { markSiteUnsynced } from "./syncStore";

/**
 * Shared handler for when an automatic `action/deploy_next/` (auto-sync) call
 * fails after the underlying save/change has already succeeded.
 *
 * Auto-sync exists so users don't have to manually click "Sync Site" the way
 * they do in actuate_admin. The gap it leaves: when the deploy call itself
 * fails, the change is persisted server-side but never deployed, and the user
 * previously received no signal at all — they'd assume their change went live.
 *
 * This handler makes that failure visible:
 *  - Re-marks the site as needing a sync (`syncNeeded`), so the flag stays
 *    semantically correct after a failed deploy (the success paths set it to
 *    "false").
 *  - Alerts the user that their change was saved but not deployed, and tells
 *    them how to recover (Sync Site on the site's About page).
 *
 * Intended to be passed as the `errorHandlingFunction` to
 * `universalAPIErrorHandler` at every automatic `deploy_next` call site.
 * `universalAPIErrorHandler` passes an `APIErrorContext` on non-terminal
 * failures (a scheduled retry, or the browser being offline); in those cases
 * the sync flag is still set but the alert is suppressed — the retry may
 * succeed, and if it fails terminally this handler fires again without
 * context and alerts then.
 */
export const handleDeployNextFailure = (context?: APIErrorContext): void => {
  markSiteUnsynced();
  if (context?.willRetry || context?.offline) {
    return;
  }
  alert(
    "Your changes were saved, but automatically deploying them to the site failed. " +
      'Please open the site\'s About page and click "Sync Site" to deploy your changes.'
  );
};
